import { useState, useCallback, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CGAHierarchyAppsQuery } from '../CGAHierarchyAppsQuery';
import {
  CGAAppItem,
  CGAHierarchyAppsQueryResponse,
  HierarchyLevel,
} from '../types';
import { ResponsibleParty } from '../../insights/types/RoleTypes';

/**
 * State returned by the useHierarchyApplications hook
 */
export interface UseHierarchyApplicationsState {
  applications: CGAAppItem[];
  selectedApps: CGAAppItem[];
  loading: boolean;
  error: Error | null;
  hasSearched: boolean;
  targetLogin: string;
  targetUserName: string;
  targetUserTitle: string;
  hierarchyLevel: HierarchyLevel;
  selectedParty: ResponsibleParty;
  partySearchResetKey: number;
}

/**
 * Actions returned by the useHierarchyApplications hook
 */
export interface UseHierarchyApplicationsActions {
  setSelectedApps: React.Dispatch<React.SetStateAction<CGAAppItem[]>>;
  setHierarchyLevel: (level: HierarchyLevel) => void;
  handlePartySelect: React.Dispatch<React.SetStateAction<ResponsibleParty>>;
  handleGenerateReport: () => Promise<void>;
  handleHierarchyChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  handleClearSearch: () => void;
}

/**
 * Custom hook for managing hierarchy applications state and fetching
 * @returns State and actions for hierarchy applications
 */
export const useHierarchyApplications = (): [
  UseHierarchyApplicationsState,
  UseHierarchyApplicationsActions,
] => {
  const searchRunIdRef = useRef(0);

  const [loadHierarchyApps, { fetchMore }] =
    useLazyQuery<CGAHierarchyAppsQueryResponse>(CGAHierarchyAppsQuery, {
      fetchPolicy: 'network-only',
    });

  // State management
  const [applications, setApplications] = useState<CGAAppItem[]>([]);
  const [selectedApps, setSelectedApps] = useState<CGAAppItem[]>([]);
  const [hierarchyLevel, setHierarchyLevel] = useState<HierarchyLevel>(
    HierarchyLevel.DIRECT_REPORTS,
  );
  const [selectedParty, setSelectedParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [partySearchResetKey, setPartySearchResetKey] = useState(0);
  const [targetLogin, setTargetLogin] = useState<string>('');
  const [targetUserName, setTargetUserName] = useState<string>('');
  const [targetUserTitle, setTargetUserTitle] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch all applications for a login with pagination
   */
  const fetchAllApplicationsForLogin = useCallback(
    async (
      login: string,
      level: HierarchyLevel,
    ): Promise<{
      applications: CGAAppItem[];
      userName: string;
      userTitle: string;
    }> => {
      const limit = 100;
      const PAGE_CONCURRENCY = 5;

      // Page 0 must run first to get totalCount and user metadata
      const firstResponse = await loadHierarchyApps({
        variables: {
          login,
          pagination: { limit, offset: 0 },
          hierarchyLevel: level,
        },
      });

      const userDisplayName = firstResponse.data?.identity?.user?.displayName;
      const userTitle = firstResponse.data?.identity?.user?.title;
      const firstPortfolio =
        firstResponse.data?.identity?.user?.resources?.applicationPortfolio;
      const totalCount =
        typeof firstPortfolio?.totalCount === 'number'
          ? firstPortfolio.totalCount
          : null;
      const firstPageApps = (firstPortfolio?.applications ??
        []) as CGAAppItem[];

      const fetchedApplications: CGAAppItem[] = [...firstPageApps];

      if (totalCount !== null && fetchedApplications.length < totalCount) {
        // Precompute all remaining page offsets and fetch them in parallel chunks
        const remainingOffsets: number[] = [];
        for (let offset = limit; offset < totalCount; offset += limit) {
          remainingOffsets.push(offset);
        }

        for (let i = 0; i < remainingOffsets.length; i += PAGE_CONCURRENCY) {
          const offsetChunk = remainingOffsets.slice(i, i + PAGE_CONCURRENCY);
          const settled = await Promise.allSettled(
            offsetChunk.map(offset =>
              fetchMore
                ? fetchMore({
                    variables: {
                      login,
                      pagination: { limit, offset },
                      hierarchyLevel: level,
                    },
                    updateQuery: (previous, { fetchMoreResult }) =>
                      fetchMoreResult ?? previous,
                  })
                : loadHierarchyApps({
                    variables: {
                      login,
                      pagination: { limit, offset },
                      hierarchyLevel: level,
                    },
                  }),
            ),
          );

          settled.forEach(result => {
            if (result.status === 'fulfilled') {
              const portfolio =
                result.value.data?.identity?.user?.resources
                  ?.applicationPortfolio;
              const pageApps = (portfolio?.applications ?? []) as CGAAppItem[];
              fetchedApplications.push(...pageApps);
            }
            // Silently skip failed pages — the rest of the list is still usable
          });
        }
      } else if (totalCount === null && firstPageApps.length > 0) {
        // Fallback: totalCount unknown, fetch sequentially until an empty page
        let offset = firstPageApps.length;
        let hasMore = firstPageApps.length === limit;
        while (hasMore) {
          const response = fetchMore
            ? await fetchMore({
                variables: {
                  login,
                  pagination: { limit, offset },
                  hierarchyLevel: level,
                },
                updateQuery: (previous, { fetchMoreResult }) =>
                  fetchMoreResult ?? previous,
              })
            : await loadHierarchyApps({
                variables: {
                  login,
                  pagination: { limit, offset },
                  hierarchyLevel: level,
                },
              });
          const portfolio =
            response.data?.identity?.user?.resources?.applicationPortfolio;
          const pageApps = (portfolio?.applications ?? []) as CGAAppItem[];
          if (!pageApps.length) {
            hasMore = false;
          } else {
            fetchedApplications.push(...pageApps);
            offset += pageApps.length;
            hasMore = pageApps.length === limit;
          }
        }
      }

      const filteredApplications = fetchedApplications.filter(app => {
        const status = (app.status || '').toLowerCase();
        return status === 'active' || status === 'pre-prod';
      });

      const normalizedApplications = filteredApplications.map(app => {
        const appId =
          typeof app.applicationId === 'string'
            ? parseInt(app.applicationId, 10)
            : app.applicationId;

        return {
          ...app,
          applicationId: appId,
        };
      });

      return {
        applications: normalizedApplications,
        userName: userDisplayName || '',
        userTitle: userTitle || '',
      };
    },
    [fetchMore, loadHierarchyApps],
  );

  /**
   * Handle party selection from PartySearch
   */
  const handlePartySelect: React.Dispatch<
    React.SetStateAction<ResponsibleParty>
  > = useCallback(
    value => {
      searchRunIdRef.current += 1;
      const party = typeof value === 'function' ? value(selectedParty) : value;
      setSelectedParty(party);
      setHasSearched(false);
      setTargetLogin('');
      setTargetUserName('');
      setTargetUserTitle('');
      setApplications([]);
      setSelectedApps([]);
      setLoading(false);
      setError(null);
    },
    [selectedParty],
  );

  /**
   * Clear search and reset state
   */
  const handleClearSearch = useCallback(() => {
    searchRunIdRef.current += 1;
    setHasSearched(false);
    setTargetLogin('');
    setTargetUserName('');
    setTargetUserTitle('');
    setSelectedParty({} as ResponsibleParty);
    setPartySearchResetKey(prev => prev + 1);
    setApplications([]);
    setSelectedApps([]);
    setLoading(false);
    setError(null);
  }, []);

  /**
   * Handle Generate Report button click
   */
  const handleGenerateReport = useCallback(async () => {
    if (!selectedParty?.login) {
      return;
    }

    searchRunIdRef.current += 1;
    const runId = searchRunIdRef.current;

    setTargetLogin(selectedParty.login);
    setHasSearched(true);
    setApplications([]);
    setSelectedApps([]);

    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllApplicationsForLogin(
        selectedParty.login,
        hierarchyLevel,
      );
      if (runId !== searchRunIdRef.current) {
        return;
      }
      setTargetUserName(result.userName);
      setTargetUserTitle(result.userTitle);
      setApplications(result.applications);
    } catch (err) {
      if (runId !== searchRunIdRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(new Error(message));
    } finally {
      if (runId === searchRunIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedParty?.login, hierarchyLevel, fetchAllApplicationsForLogin]);

  /**
   * Handle hierarchy level change
   */
  const handleHierarchyChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const newLevel = event.target.value as HierarchyLevel;
      setHierarchyLevel(newLevel);
      setSelectedApps([]); // Clear selection when hierarchy changes

      // Refetch with new hierarchy if we have a target user
      if (targetLogin) {
        searchRunIdRef.current += 1;
        const runId = searchRunIdRef.current;
        setLoading(true);
        setError(null);
        void (async () => {
          try {
            const result = await fetchAllApplicationsForLogin(
              targetLogin,
              newLevel,
            );
            if (runId !== searchRunIdRef.current) {
              return;
            }
            setTargetUserName(result.userName);
            setTargetUserTitle(result.userTitle);
            setApplications(result.applications);
          } catch (err) {
            if (runId !== searchRunIdRef.current) {
              return;
            }
            const message =
              err instanceof Error ? err.message : 'Unknown error';
            setError(new Error(message));
          } finally {
            if (runId === searchRunIdRef.current) {
              setLoading(false);
            }
          }
        })();
      }
    },
    [targetLogin, fetchAllApplicationsForLogin],
  );

  /**
   * Handle setting hierarchy level directly
   */
  const handleSetHierarchyLevel = useCallback((level: HierarchyLevel) => {
    setHierarchyLevel(level);
  }, []);

  const state: UseHierarchyApplicationsState = {
    applications,
    selectedApps,
    loading,
    error,
    hasSearched,
    targetLogin,
    targetUserName,
    targetUserTitle,
    hierarchyLevel,
    selectedParty,
    partySearchResetKey,
  };

  const actions: UseHierarchyApplicationsActions = {
    setSelectedApps,
    setHierarchyLevel: handleSetHierarchyLevel,
    handlePartySelect,
    handleGenerateReport,
    handleHierarchyChange,
    handleClearSearch,
  };

  return [state, actions];
};
